/**
 * Sessionsminne för AI Look Preview: den uppladdade bilden — en per utsnitt,
 * helkropp för plagg och fötterna för skor — är konstanten i flödet; plagget
 * eller skon är det som byts. Bilderna och färdiga resultat sparas här
 * (modul-scope, överlever modal-stängning inom samma SPA-session) så att
 * kunden varken behöver ladda upp igen eller vänta en gång till för något som
 * redan genererats.
 */

import type {
  TryOnConsent,
  TryOnFraming,
  TryOnJobResponse,
  TryOnProductDetail,
} from '../../types/tryOn';

export interface TryOnSessionPhoto {
  /** Signerad referens — identiteten på bilden, och det enda backend tar emot. */
  uploadId: string;
  /** Visnings-URL. Skickas aldrig till backend som jobbindata. */
  previewUrl: string;
  /** ISO-timestamp när backend raderar bilden. */
  expiresAt: string;
}

const storedPhotos: Partial<Record<TryOnFraming, TryOnSessionPhoto>> = {};
// Varje uppladdning sessionen gjort, även utgångna och ersatta: filen ligger kvar
// på servern tills den raderas, fast getSessionPhoto släppt den ur minnet.
const sessionUploadIds = new Set<string>();
let storedConsent = false;
/** Färdiga previews: nyckel = `${uploadId}::${productKey}`. */
const resultCache = new Map<string, TryOnJobResponse>();

const isExpired = (photo: TryOnSessionPhoto | null) =>
  !photo || new Date(photo.expiresAt).getTime() <= Date.now();

export function getSessionPhoto(framing: TryOnFraming = 'full'): TryOnSessionPhoto | null {
  const photo = storedPhotos[framing] ?? null;
  if (isExpired(photo)) {
    delete storedPhotos[framing];
    return null;
  }
  return photo;
}

export function setSessionPhoto(photo: TryOnSessionPhoto, framing: TryOnFraming = 'full'): void {
  storedPhotos[framing] = photo;
  sessionUploadIds.add(photo.uploadId);
}

/** Tömmer ALLA utsnitt — "Radera min bild nu" gäller varje bild i sessionen. */
export function clearSessionPhoto(): void {
  for (const framing of Object.keys(storedPhotos) as TryOnFraming[]) {
    delete storedPhotos[framing];
  }
  sessionUploadIds.clear();
}

export function getAllSessionUploadIds(): string[] {
  return [...sessionUploadIds];
}

export function getAllSessionPhotos(): TryOnSessionPhoto[] {
  return (['full', 'feet'] as const)
    .map((framing) => getSessionPhoto(framing))
    .filter((photo): photo is TryOnSessionPhoto => photo !== null);
}

/** Kundens valda, ännu inte uppladdade fil — med utsnittet den valdes för. */
export interface PhotoSelection {
  file: File;
  framing: TryOnFraming;
}

/**
 * En vald fil hör till sitt utsnitt. Utan grinden kunde en kvarliggande
 * helkroppsbild laddas upp och signeras som fotbild (granskning 2026-10-07),
 * och skovägen genererar om ansiktet — den kompositerar inget huvud.
 */
export function selectedFileFor(
  selection: PhotoSelection | null,
  framing: TryOnFraming,
): File | null {
  return selection && selection.framing === framing ? selection.file : null;
}

export function getSessionConsent(): boolean {
  return storedConsent;
}

export function setSessionConsent(consent: boolean): void {
  storedConsent = consent;
}

/** Valfritt GDPR-samtycke till förlängd lagring (aldrig obligatoriskt). */
let storedDevRetentionConsent = false;

// Höjs när try_on_consent_main eller try_on_consent_retention ändras, så att
// loggen visar vilken text kunden faktiskt godkände.
const PLAYGROUND_CONSENT_VERSION = 'playground-tryon-v1';

export function getSessionConsentRecord(): TryOnConsent | null {
  if (!storedConsent) return null;
  return {
    granted: true,
    version: PLAYGROUND_CONSENT_VERSION,
    at: new Date().toISOString(),
    retention: storedDevRetentionConsent ? '30d' : '24h',
  };
}

export function getSessionDevRetentionConsent(): boolean {
  return storedDevRetentionConsent;
}

export function setSessionDevRetentionConsent(consent: boolean): void {
  storedDevRetentionConsent = consent;
}

/** Töm alla cachade resultat (t.ex. efter "Radera min bild nu"). */
export function clearResultCache(): void {
  resultCache.clear();
}

/** Alla cachade jobb — så "Radera min bild nu" kan radera allt server-side. */
export function getAllCachedResults(): TryOnJobResponse[] {
  return Array.from(resultCache.values());
}

const cacheKey = (uploadId: string, productKey: string) => `${uploadId}::${productKey}`;

export function getCachedResult(uploadId: string, productKey: string): TryOnJobResponse | null {
  const cached = resultCache.get(cacheKey(uploadId, productKey));
  if (!cached) return null;
  if (new Date(cached.expiresAt).getTime() <= Date.now()) {
    resultCache.delete(cacheKey(uploadId, productKey));
    return null;
  }
  return cached;
}

export function setCachedResult(
  uploadId: string,
  productKey: string,
  job: TryOnJobResponse,
): void {
  resultCache.set(cacheKey(uploadId, productKey), job);
}

export function getCachedProductKeys(uploadId: string): Set<string> {
  const keys = new Set<string>();
  const prefix = `${uploadId}::`;
  for (const key of resultCache.keys()) {
    if (key.startsWith(prefix)) keys.add(key.slice(prefix.length));
  }
  return keys;
}

/** Produktdetaljer (Produktbeskrivning-accordion) — hämtas en gång per produkt. */
const detailCache = new Map<string, TryOnProductDetail | null>();

/** Nyckeln bär SPRÅKET: detaljerna översätts per språk, så en cache på bara
 *  produkt-id skulle servera fel språk efter ett språkbyte. */
const detailKey = (productId: string, lang?: string) => `${productId}::${lang ?? ''}`;

export function getCachedProductDetail(
  productId: string,
  lang?: string,
): TryOnProductDetail | null | undefined {
  return detailCache.get(detailKey(productId, lang));
}

export function setCachedProductDetail(
  productId: string,
  lang: string | undefined,
  detail: TryOnProductDetail | null,
): void {
  detailCache.set(detailKey(productId, lang), detail);
}
